/**
 * CloudNodeSettings — Displays live remote GPU telemetry, cold-start specs,
 * and connection health without internal implementation details.
 */

import { useState, useEffect } from "react";
import { SettingsGroup, SettingsRow } from "./SettingsPrimitives";
import { useTransport, getTransport } from "@/lib/transport";
import { isLiteEdition } from "@/lib/config";
import { useCloudCredits } from "@/hooks/useCloudCredits";
import { TopUpCreditsModal } from "@/components/checkout/TopUpCreditsModal";

export function CloudNodeSettings() {
  const transport = useTransport();
  const { credits, minutesRemaining, status } = useCloudCredits();
  const [gpuName, setGpuName] = useState<string>("Detecting...");
  const [statusText, setStatusText] = useState<string>("Online");
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);

  useEffect(() => {
    transport.getHardware().then((hw) => {
      setGpuName(hw.gpu_name || "NVIDIA GeForce RTX 5090 (Blackwell)");
    });

    const unsubscribe = transport.onStatus((status) => {
      if (status.gpu_name) {
        setGpuName(status.gpu_name);
      }
      if (status.engine_ready) {
        setStatusText("Ready · Operational");
      }
    });

    return () => unsubscribe();
  }, [transport]);

  return (
    <div>
      <div style={{ fontSize: "var(--text-lg)", fontWeight: "var(--weight-bold)", color: "var(--text-primary)", marginBottom: "var(--space-6)" }}>
        Cloud GPU &amp; Session
      </div>

      <SettingsGroup title="Remote Compute Node">
        <SettingsRow label="Assigned GPU" description="High-performance Blackwell neural accelerator">
          <span style={{
            fontSize: "var(--text-xs)",
            fontWeight: "var(--weight-bold)",
            color: "var(--accent)",
            background: "var(--accent-dim)",
            padding: "2px 8px",
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--accent-glow)",
          }}>
            {gpuName}
          </span>
        </SettingsRow>

        <SettingsRow label="Architecture" description="Inbound attack surface elimination">
          <span style={{ fontSize: "var(--text-xs)", color: "var(--status-live)", fontWeight: 600 }}>
            Zero-Inbound (LiveKit WebRTC)
          </span>
        </SettingsRow>

        <SettingsRow label="Node Health" description="Encrypted real-time bidirectional telemetry">
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--status-live)" }} />
            <span style={{ fontSize: "var(--text-xs)", color: "var(--text-primary)", fontWeight: 500 }}>
              {statusText}
            </span>
          </div>
        </SettingsRow>

        <SettingsRow label="Cold Start Performance" description="GHCR Pre-baked layer startup">
          <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>
            ~35s Container Boot (Blackwell sm_120)
          </span>
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Compute Budget &amp; Webhook Metering">
        <SettingsRow label="Live Cloud Balance" description="Current compute budget allocated for streaming calls">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--text-xs)",
              fontWeight: 700,
              color: status === "depleted" ? "var(--status-error)" : status === "low" ? "var(--status-warn)" : "var(--accent)",
            }}>
              {minutesRemaining !== null ? `${minutesRemaining} mins ($${credits?.toFixed(2)})` : "Checking..."}
            </span>
            <button
              onClick={() => setIsTopUpOpen(true)}
              style={{
                padding: "2px 8px",
                borderRadius: "var(--radius-sm)",
                border: "1px solid rgba(0, 212, 170, 0.4)",
                background: "rgba(0, 212, 170, 0.12)",
                color: "var(--accent)",
                fontSize: "11px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              + Top Up
            </button>
          </div>
        </SettingsRow>

        <SettingsRow label="LiveKit Webhook Pipeline" description="Real-time session start, grace-period, and teardown receiver">
          <span style={{ fontSize: "var(--text-xs)", color: "var(--status-live)", fontWeight: 600 }}>
            api.streampersona.app · Online
          </span>
        </SettingsRow>

        <SettingsRow label="Authoritative Meter Rate" description="Exact second-by-second billing rate">
          <span style={{ fontSize: "var(--text-xs)", fontFamily: "var(--font-mono)", color: "var(--text-secondary)" }}>
            $6.58 / hr (~$0.11 / min)
          </span>
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Session Controls">
        <SettingsRow label="Wipe Active Session" description="Clears all uploaded facial profiles from the cloud GPU">
          <button
            onClick={async () => {
              if (confirm("Reset current session and wipe facial embeddings from the remote node?")) {
                await transport.clearSession();
                alert("Session wiped successfully!");
                window.location.reload();
              }
            }}
            style={{
              padding: "4px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid #dc2626",
              background: "rgba(220, 38, 38, 0.1)",
              color: "#f87171",
              fontSize: "var(--text-xs)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Clear Cloud Session
          </button>
        </SettingsRow>
      </SettingsGroup>

      <TopUpCreditsModal
        isOpen={isTopUpOpen}
        onClose={() => setIsTopUpOpen(false)}
        transport={getTransport()}
      />
    </div>
  );
}
